/*
 * LandRegisterDepartment.tsx
 * Author: fwunderlich
 * Date: 13.03.2024
 *
 * Copyright: DMT GmbH & Co. KG
 */

import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Mapping, MappingType, removeMapping} from '../../../redux/slices/MappingSlice';
import {useAppDispatch, useAppSelector} from '../../../redux/hooks';
import CustomDataTable, {ColumnObject} from '../../global/CustomDataTable';
import {getColumnsParcel, getFiltersParcel, Parcel, Person} from '../../functions/GlobalData';
import {CustomConfirmDialog} from '../../global/CustomConfirmDialog';
import {checkPermission} from '../../../functions/functionLibrary';
import {permissions} from '../../../config/permissions';
import {Button} from 'primereact/button';
import {useNavigate} from 'react-router-dom';
import {useGetPersonQuery} from '../../../redux/rtk/injectedPersonApi';
import {PersonMapping} from '../person/PersonMapping';
import moment from 'moment';
import {useGetParcelQuery} from '../../../redux/rtk/injectedParcelApi';
import {faPerson} from '@fortawesome/free-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {LandRegisterPersonOrParcelMapping} from '../mapping/LandRegisterPersonOrParcelMapping';

type Props = {
    projectId: string,
    grundbuchId: string,
    isDirty: boolean,
    onChange: () => void,
    className?: string,
    skip?: boolean
}

/**
 * Form for editing and creating of users
 * @param props
 * @constructor
 */
export const LandRegisterDepartment = (props: Props): JSX.Element => {

    const {t} = useTranslation(['common', 'globalData', 'toasts']);

    const navigate = useNavigate();

    const mappings: Mapping[] = useAppSelector((state: any) => state.mappingData.mappings);
    const dispatch = useAppDispatch();

    const [values, setValues] = useState<any>([]);
    const [selectedValue, setSelectedValue] = useState<Person | undefined>(undefined);
    const [showMappingAdd, setShowMappingAdd] = useState<boolean>(false);
    const [activeMappings, setActiveMappings] = useState<any[]>([]);

    const {
        data: person = [], isLoading,
        isSuccess,
        isFetching,
        isError,
        error,
        refetch,
    } = useGetPersonQuery({
        projectId: props.projectId,
        commonData: true,
        // flurstueckId: props.dataIds ? props.dataIds.length > 0 ? `["${props.dataIds.join('","')}"]` : [] : undefined,
        mapping: {grundbuch_id: props.grundbuchId},
    }, {skip: props.skip != null ? props.skip : false});

    const {data: persons = []} = useGetPersonQuery({
        // projectId: props.projectId,
        projectId: '',
        personId: '',
        commonData: true
    });

    useEffect(() => {
        addValues();
    }, []);

    useEffect(() => {
        if (person) {
            setValues(person);
            setActiveMappings(person ? person.map((item: Person) => {
                return item.person_id;
            }) : []);
        }
    }, [isSuccess]);

    useEffect(() => {
        addValues();
    }, [JSON.stringify(mappings)]);

    const addValues = () => {
        setValues((prevState: any) => {
            let temp = JSON.parse(JSON.stringify(prevState));

            if (temp) {
                for (let i = 0; i < mappings.length; i++) {
                    const map = mappings[i];

                    if (map) {
                        let item = temp.find((x: Person) => x.person_id === map.id);

                        if (item) {
                            if (map.remove) {
                                item.is_deleted = true;
                            }
                        } else {
                            if (!map.remove) {
                                let tempItem = persons.find((x: Person) => x.person_id === map.id);
                                if (tempItem) {
                                    temp.push({...tempItem, ...map});
                                }
                            }
                        }
                    }
                }
            }
            return temp;
        });
    };

    const clickAddMapping = (e: any) => {
        setShowMappingAdd(true);
    };

    const onHide = () => {
        setShowMappingAdd(false);
        setSelectedValue(undefined);
    };

    return (<div className={props.className}>
        <div>
            <div className={'flex mb-2'}>
                <h4 className={'m-0  ' +
                'white-space-nowrap'}>{t('globalData:global.mapping.abteilung1')}</h4>
                <div className={'w-full grid grid-nogutter justify-content-end'}>
                    <Button
                        className="p-button-success p-button-outlined p-button-icon-only"
                        type="button" icon="pi pi-plus"
                        onClick={clickAddMapping}/>
                </div>
            </div>
            <div className={'overflow-x-auto'}>
                <table className={'w-full border-1 text-center'} style={{borderSpacing: 0}}>
                    <tr>
                        <th className={'border-1'}></th>
                        <th className={'border-1'}>{t('globalData:landRegister.abteilung1.col1')}</th>
                        <th className={'border-1'}>{t('globalData:landRegister.abteilung1.col2')}</th>
                        <th className={'border-1'}>{t('globalData:landRegister.abteilung1.col3')}</th>
                        <th className={'border-1'}>{t('globalData:landRegister.abteilung1.col4')}</th>
                    </tr>
                    <tr>
                        <th className={'border-1'}></th>
                        <th className={'border-1'}>{t('globalData:landRegister.abteilung1.col5')}</th>
                        <th className={'border-1'}>{t('globalData:landRegister.abteilung1.col6')}</th>
                        <th className={'border-1'}>{t('globalData:landRegister.abteilung1.col7')}</th>
                        <th className={'border-1'}>{t('globalData:landRegister.abteilung1.col8')}</th>
                    </tr>

                    {values && values.map((item: Person) => {
                        return <tr className={'text-left'} style={item.is_deleted ? {
                            textDecoration: 'line-through',
                            textDecorationColor: 'red'
                        } : {}}>
                            <td className={'flex'}>
                                {checkPermission(permissions.editLandregister) && <>
                                    <Button
                                        className={'p-button-rounded p-button-text'} style={{float: 'right'}}
                                        type={'button'}
                                        icon={'pi pi-pencil'}
                                        onClick={() => {
                                            setSelectedValue(item);
                                        }}/>
                                    <Button
                                        className={'p-button-rounded p-button-danger p-button-text'}
                                        style={{float: 'right'}}
                                        type={'button'}
                                        icon={'pi pi-trash'}
                                        onClick={() => {
                                            CustomConfirmDialog({
                                                message: t('globalData:global.mapping.removeMappingPerson'),
                                                header: t('confirmDialog:deleteDialogHeader'),
                                                translation: t,
                                                onConfirm: () => {
                                                    const mapping: Mapping = {
                                                        id: item.person_id,
                                                        mapping_type: MappingType.personLandRegister,
                                                        project_id: props.projectId,
                                                        common_data: true,
                                                        remove: true
                                                    };

                                                    mapping.grundbuch_id = props.grundbuchId;

                                                    dispatch(removeMapping(mapping));
                                                    props.onChange();
                                                }
                                            });
                                        }}/>
                                    <Button
                                        className={'p-button-rounded p-button-text'}
                                        style={{float: 'right'}}
                                        type={'button'}
                                        // icon={'pi pi-bolt'}
                                        onClick={() => {
                                            const url = `/project/${props.projectId}/#globalData_1_${item.person_id}`;
                                            if (props.isDirty) {
                                                CustomConfirmDialog({
                                                    message: t('dialogs.saveChangesMessage'),
                                                    header: t('dialogs.saveChangesHeader'),
                                                    translation: t,
                                                    onConfirm: () => {
                                                        navigate(url);
                                                    },
                                                    rejectClassName: 'float-right p-button-secondary'
                                                });
                                            } else {
                                                navigate(url);
                                            }
                                        }}><FontAwesomeIcon className="mx-1" icon={faPerson}/></Button>
                                </>}
                            </td>
                            <td className={'border-1 p-1'}>{item.lfdnr_eintragung}</td>
                            <td className={'border-1 p-1'}>
                                <div>{item.name + (!item.ist_juristisch ? ', ' + item.vorname : '')}</div>
                                {!item.ist_juristisch &&
                                <div>{item.geburtsname}</div>}
                                {!item.ist_juristisch && item.geburtsdatum &&
                                <div>{t('globalData:persons.geborenAm') + ' ' + moment(item.geburtsdatum).format(t('dateformat'))}</div>}
                            </td>
                            <td className={'border-1 p-1'}>{item.lfdnr_bestandsverzeichnis}</td>
                            <td className={'border-1 p-1'}>{item.grundlage_eintragung}</td>
                        </tr>;
                    })}
                </table>
            </div>
        </div>
        <LandRegisterPersonOrParcelMapping projectId={props.projectId} selectedValue={selectedValue}
                                           showMappingAdd={showMappingAdd} mappingId={props.grundbuchId}
                                           mappingType={MappingType.personLandRegister}
                                           activeMappings={activeMappings} setValues={setValues}
                                           onChange={props.onChange}
                                           onHide={onHide}/>
    </div>);
};
